import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Stack, Plus, Trash, PencilSimple, CheckCircle, FileArrowDown, UploadSimple, CaretRight, CaretDown, X, ShieldCheck, Buildings, SealCheck, Export } from "@phosphor-icons/react";

const TABS = [
  { id: "bes_assets", label: "BES Assets" },
  { id: "register", label: "BES Cyber Systems" },
  { id: "r1", label: "R1 List & Sign-off" },
  { id: "overview", label: "Overview" },
];

const IMPACT_BADGE = {
  high: "bg-red-100 text-red-800 border-red-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-blue-100 text-blue-800 border-blue-300",
};
const impactLabel = (s) => ({ high: "High", medium: "Medium", low: "Low" }[s] || s);

const REVIEW_BADGE = {
  current: "bg-green-100 text-green-800 border-green-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  overdue: "bg-red-100 text-red-800 border-red-300",
  never: "bg-gray-100 text-gray-600 border-gray-300",
};
const reviewLabel = (s) => ({ current: "Current", due_soon: "Review due", overdue: "Overdue", never: "Not approved" }[s] || s);

const TYPE_BADGE = {
  BCA: "bg-slate-100 text-slate-700 border-slate-300",
  EACMS: "bg-purple-100 text-purple-700 border-purple-300",
  PACS: "bg-teal-100 text-teal-700 border-teal-300",
  PCA: "bg-orange-100 text-orange-700 border-orange-300",
};

export default function Cip002Assets() {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [tab, setTab] = useState("register");
  const [summary, setSummary] = useState(null);
  const [systems, setSystems] = useState([]);
  const [besAssets, setBesAssets] = useState([]);
  const [besTypes, setBesTypes] = useState([]);
  const [meta, setMeta] = useState({ asset_types: ["BCA", "EACMS", "PACS", "PCA"], asset_labels: {}, impacts: ["high", "medium", "low"], device_functions: [] });
  const [expanded, setExpanded] = useState(null);
  const [assets, setAssets] = useState({});
  const [editBcs, setEditBcs] = useState(null);
  const [assetForm, setAssetForm] = useState(null);
  const [editBes, setEditBes] = useState(null);
  const csvRef = useRef(null);

  const loadSummary = useCallback(() => api.get("/cip002/summary").then((r) => {
    setSummary(r.data);
    setMeta((m) => ({ ...m, device_functions: r.data.categorization?.device_functions || [] }));
  }).catch(() => {}), []);
  const loadSystems = useCallback(() => api.get("/cip002/bcs").then((r) => {
    setSystems(r.data.systems);
    setMeta((m) => ({ ...m, asset_types: r.data.asset_types, asset_labels: r.data.asset_labels, impacts: r.data.impacts }));
  }).catch(() => {}), []);
  const loadBes = useCallback(() => api.get("/cip002/bes-assets").then((r) => { setBesAssets(r.data.bes_assets); setBesTypes(r.data.types); }).catch(() => {}), []);

  useEffect(() => { loadSummary(); loadSystems(); loadBes(); api.post("/cip002/process/run").catch(() => {}); }, [loadSummary, loadSystems, loadBes]);

  const loadAssets = useCallback((bid) => api.get(`/cip002/bcs/${bid}/assets`).then((r) => setAssets((p) => ({ ...p, [bid]: r.data.assets }))).catch(() => {}), []);
  const toggleExpand = (bid) => {
    if (expanded === bid) { setExpanded(null); return; }
    setExpanded(bid);
    if (!assets[bid]) loadAssets(bid);
  };

  const refresh = () => { loadSummary(); loadSystems(); loadBes(); };

  const delBcs = async (b) => {
    if (!window.confirm(`Delete "${b.name}" and its ${b.asset_total} associated asset(s)?`)) return;
    try { await api.delete(`/cip002/bcs/${b.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reviewBcs = async (b) => {
    if (!window.confirm(`Record a review for "${b.name}" today? Resets its 15-month review clock.`)) return;
    try { await api.post(`/cip002/bcs/${b.id}/review`); toast.success("Review recorded"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAsset = async (a) => {
    if (!window.confirm(`Remove asset "${a.name}"?`)) return;
    try { await api.delete(`/cip002/assets/${a.id}`); toast.success("Removed"); loadAssets(a.bcs_id); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delBes = async (b) => {
    if (!window.confirm(`Delete BES Asset "${b.name}"? Linked BES Cyber Systems are kept but unlinked.`)) return;
    try { await api.delete(`/cip002/bes-assets/${b.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cip002/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.bes_assets_created || 0} BES Asset(s), ${data.bcs_created} BCS, ${data.assets_imported} asset(s)`);
      if (data.errors?.length) data.errors.slice(0, 3).forEach((m) => toast.warning(m));
      setAssets({}); setExpanded(null); refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openUrl = async (path) => {
    try { await downloadBlob(path); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const populateErt = async () => {
    if (!window.confirm("Push all BES Assets, BES Cyber Systems and Cyber Assets into the CIP Evidence Request Tool (ERT)? Existing manual ERT entries are preserved.")) return;
    try {
      const { data } = await api.post("/cip002/populate-ert");
      toast.success(`ERT populated: ${data.bes_assets} BES Assets, ${data.bcs} BCS, ${data.ca} Cyber Assets`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-full p-6" data-testid="cip002-page">
      <div className="flex items-center gap-2 mb-1">
        <Stack size={26} weight="duotone" />
        <h1 className="text-2xl font-bold">CIP-002 Identification &amp; Categorization</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Identify and categorize your BES Cyber Systems (CIP-002-5.1a R1). The required output is the list of BCS by
        impact (High / Medium / Low — any may be null). The CIP Senior Manager approves the identifications at least
        once every {summary?.review_months || 15} months (R2). Cyber Asset detail is captured to seed your Evidence Request Tool.
      </p>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="cip002-summary">
          <StatCard label="BES Assets" value={summary.bes_asset_count} />
          <StatCard label="BES Cyber Systems" value={summary.bcs_count} />
          <StatCard label="High / Med / Low BCS" value={`${summary.by_impact.high} / ${summary.by_impact.medium} / ${summary.by_impact.low}`} />
          <StatCard label="Cyber Assets" value={summary.asset_count} />
          <StatCard label="R2 Approval" value={reviewLabel(summary.categorization?.approval?.status)}
            tone={summary.categorization?.approval?.status === "overdue" ? "text-red-600" : summary.categorization?.approval?.status === "due_soon" ? "text-amber-600" : ""} />
        </div>
      )}

      <div className="flex gap-1 border-b border-border mb-4" data-testid="cip002-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`cip002-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ---- BES ASSETS TAB ---- */}
      {tab === "bes_assets" && (
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {canEdit && <Button size="sm" className="gap-1" onClick={() => setEditBes({})} data-testid="cip002-add-bes-btn"><Plus size={14} /> Add BES Asset</Button>}
            <span className="text-xs text-muted-foreground">BES Assets are the identification starting point (Control Centers, Substations, Data Centers…). {besAssets.length} recorded</span>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="text-sm min-w-full" data-testid="cip002-bes-table">
              <thead className="bg-secondary"><tr>{["BES Asset", "Type", "Impact Flags", "Location", "BCS", ""].map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {besAssets.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip002-bes-empty">No BES Assets yet. Start by adding your Control Centers, Substations, etc.</td></tr>
                ) : besAssets.map((b) => (
                  <tr key={b.id} className="border-t border-border hover:bg-secondary/40" data-testid={`cip002-bes-row-${b.id}`}>
                    <td className="px-3 py-2 font-medium">{b.name}{b.bes_asset_id && <span className="ml-2 text-xs text-muted-foreground">({b.bes_asset_id})</span>}</td>
                    <td className="px-3 py-2">{b.asset_type}</td>
                    <td className="px-3 py-2 text-xs">
                      {["high_impact", "medium_impact", "low_impact"].filter((k) => b[k]).map((k) => impactLabel(k.split("_")[0])).join(", ") || "—"}
                      {b.erc && <span className="ml-1 text-[10px] border border-slate-300 px-1 rounded">ERC</span>}
                    </td>
                    <td className="px-3 py-2">{b.location || "—"}</td>
                    <td className="px-3 py-2">{b.bcs_count}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditBes(b)} title="Edit" data-testid={`cip002-bes-edit-${b.id}`} className="p-1 text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                          <button onClick={() => delBes(b)} title="Delete" data-testid={`cip002-bes-del-${b.id}`} className="p-1 text-red-500 hover:text-red-700"><Trash size={16} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- BCS REGISTER TAB ---- */}
      {tab === "register" && (
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {canEdit && <Button size="sm" className="gap-1" onClick={() => setEditBcs({})} data-testid="cip002-add-bcs-btn"><Plus size={14} /> Add BES Cyber System</Button>}
            {canEdit && <Button size="sm" variant="outline" className="gap-1" onClick={() => csvRef.current?.click()} data-testid="cip002-import-btn"><UploadSimple size={14} /> Import CSV</Button>}
            <Button size="sm" variant="outline" className="gap-1" onClick={() => openUrl("/cip002/import-template")} data-testid="cip002-template-btn"><FileArrowDown size={14} /> CSV template</Button>
            <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={doImport} data-testid="cip002-import-input" />
            <span className="text-xs text-muted-foreground">{systems.length} system(s)</span>
          </div>

          <div className="border border-border overflow-x-auto">
            <table className="text-sm min-w-full" data-testid="cip002-bcs-table">
              <thead className="bg-secondary">
                <tr>{["", "BES Cyber System", "Impact", "BES Asset", "Assets (BCA/EACMS/PACS/PCA)", "Last Review", "Review Status", ""].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {systems.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip002-bcs-empty">No BES Cyber Systems yet. Add one or import your inventory.</td></tr>
                ) : systems.map((b) => {
                  const c = b.asset_counts || {};
                  return (
                  <Fragment key={b.id}>
                    <tr className="border-t border-border hover:bg-secondary/40" data-testid={`cip002-bcs-row-${b.id}`}>
                      <td className="px-2 py-2">
                        <button onClick={() => toggleExpand(b.id)} data-testid={`cip002-expand-${b.id}`} className="text-muted-foreground hover:text-foreground">
                          {expanded === b.id ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-medium">{b.name}
                        {b.categorization_basis && <div className="text-xs text-muted-foreground max-w-xs truncate" title={b.categorization_basis}>{b.categorization_basis}</div>}
                      </td>
                      <td className="px-3 py-2"><span className={`text-xs border px-1.5 py-0.5 rounded ${IMPACT_BADGE[b.impact_rating] || ""}`}>{impactLabel(b.impact_rating)}</span></td>
                      <td className="px-3 py-2">{b.bes_asset_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{c.BCA || 0} / {c.EACMS || 0} / {c.PACS || 0} / {c.PCA || 0} <span className="text-muted-foreground">({b.asset_total})</span></td>
                      <td className="px-3 py-2 whitespace-nowrap">{b.review?.last_reviewed || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-xs border px-1.5 py-0.5 rounded ${REVIEW_BADGE[b.review?.status] || ""}`}>{b.review?.status === "never" ? "Not reviewed" : reviewLabel(b.review?.status)}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {canEdit && (
                          <div className="flex items-center gap-1">
                            <button onClick={() => reviewBcs(b)} title="Record review" data-testid={`cip002-review-${b.id}`} className="p-1 text-green-600 hover:text-green-800"><ShieldCheck size={16} /></button>
                            <button onClick={() => setEditBcs(b)} title="Edit" data-testid={`cip002-edit-${b.id}`} className="p-1 text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                            <button onClick={() => delBcs(b)} title="Delete" data-testid={`cip002-del-${b.id}`} className="p-1 text-red-500 hover:text-red-700"><Trash size={16} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expanded === b.id && (
                      <tr key={`${b.id}-assets`} className="bg-secondary/30">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="overline text-muted-foreground">Associated Cyber Assets (inventory — not R1 evidence)</div>
                            {canEdit && <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => setAssetForm({ bcs_id: b.id })} data-testid={`cip002-add-asset-${b.id}`}><Plus size={12} /> Add asset</Button>}
                          </div>
                          {(assets[b.id] || []).length === 0 ? (
                            <div className="text-xs text-muted-foreground py-2" data-testid={`cip002-assets-empty-${b.id}`}>No assets recorded for this system.</div>
                          ) : (
                            <table className="text-xs w-full" data-testid={`cip002-assets-table-${b.id}`}>
                              <thead><tr className="text-muted-foreground">{["Asset", "Class", "Function", "IP", "Vendor / Model", "OS/Firmware", ""].map((h, i) => <th key={i} className="px-2 py-1 text-left font-semibold">{h}</th>)}</tr></thead>
                              <tbody>
                                {(assets[b.id] || []).map((a) => (
                                  <tr key={a.id} className="border-t border-border/60" data-testid={`cip002-asset-row-${a.id}`}>
                                    <td className="px-2 py-1 font-medium">{a.name}</td>
                                    <td className="px-2 py-1"><span className={`border px-1 py-0.5 rounded ${TYPE_BADGE[a.asset_type] || ""}`} title={meta.asset_labels[a.asset_type]}>{a.asset_type}</span></td>
                                    <td className="px-2 py-1">{a.device_function || a.device_type || "—"}</td>
                                    <td className="px-2 py-1 font-mono">{a.ip_address || "—"}</td>
                                    <td className="px-2 py-1">{[a.vendor, a.model].filter(Boolean).join(" / ") || "—"}</td>
                                    <td className="px-2 py-1">{a.os_version || "—"}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">
                                      {canEdit && (
                                        <div className="flex items-center gap-1">
                                          <button onClick={() => setAssetForm(a)} title="Edit" className="p-0.5 text-muted-foreground hover:text-foreground"><PencilSimple size={14} /></button>
                                          <button onClick={() => delAsset(a)} title="Remove" className="p-0.5 text-red-500 hover:text-red-700"><Trash size={14} /></button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- R1 LIST & SIGN-OFF TAB ---- */}
      {tab === "r1" && (
        <div className="space-y-5" data-testid="cip002-r1">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" className="gap-1" onClick={() => openUrl("/cip002/r1-export")} data-testid="cip002-r1-export-btn"><Export size={14} /> Export R1 BCS List (Word)</Button>
            {canEdit && <Button size="sm" variant="outline" className="gap-1" onClick={populateErt} data-testid="cip002-populate-ert-btn"><Buildings size={14} /> Populate Evidence Request Tool</Button>}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {["high", "medium", "low"].map((lvl) => {
              const rows = systems.filter((s) => (s.impact_rating || "").toLowerCase() === lvl);
              return (
                <div key={lvl} className="border border-border" data-testid={`cip002-r1-${lvl}`}>
                  <div className={`px-3 py-2 border-b border-border text-sm font-semibold flex items-center justify-between ${IMPACT_BADGE[lvl]}`}>
                    <span>{impactLabel(lvl)} Impact BCS</span><span>{rows.length}</span>
                  </div>
                  <div className="p-2">
                    {rows.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic px-1 py-2" data-testid={`cip002-r1-${lvl}-none`}>None identified.</div>
                    ) : rows.map((s) => (
                      <div key={s.id} className="text-sm px-1 py-1 border-b border-border/50 last:border-0">
                        <div className="font-medium">{s.name}</div>
                        {(s.bes_asset_name || s.categorization_basis) && <div className="text-xs text-muted-foreground">{[s.bes_asset_name, s.categorization_basis].filter(Boolean).join(" · ")}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {summary && <SignOffPanel cat={summary.categorization} canEdit={canEdit} onSaved={loadSummary} />}
        </div>
      )}

      {/* ---- OVERVIEW TAB ---- */}
      {tab === "overview" && summary && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="cip002-overview">
          <Card title="Impact Categorization (Attachment 1)">
            <Row label="High Impact BCS" value={summary.by_impact.high} />
            <Row label="Medium Impact BCS" value={summary.by_impact.medium} />
            <Row label="Low Impact BCS" value={summary.by_impact.low} />
            <div className="mt-3 text-xs text-muted-foreground">Declared entity impacts (applicability engine): {(summary.declared_impacts || []).map(impactLabel).join(", ") || "none"}.</div>
          </Card>
          <Card title="Asset Population">
            {meta.asset_types.map((t) => (<Row key={t} label={`${t} — ${meta.asset_labels[t] || ""}`} value={summary.by_type[t] || 0} />))}
            <Row label="BES Assets" value={summary.bes_asset_count} />
          </Card>
          <Card title="R2 Approval (CIP Senior Manager)">
            <Row label="Status" value={reviewLabel(summary.categorization?.approval?.status)} tone={summary.categorization?.approval?.status === "overdue" ? "text-red-600" : summary.categorization?.approval?.status === "due_soon" ? "text-amber-600" : ""} />
            <Row label="Approved" value={summary.categorization?.approval?.last_reviewed || "—"} />
            <Row label={`Internal target (${summary.categorization?.approval?.target_months || 12} mo)`} value={summary.categorization?.approval?.target_due || "—"} />
            <Row label="Regulatory / PNC (15 mo)" value={summary.categorization?.approval?.regulatory_due || "—"} tone={summary.categorization?.approval?.pnc ? "text-red-600" : ""} />
            <Row label="Approver" value={summary.categorization?.senior_manager_name || "—"} />
            <Row label="ERT persisted" value={summary.ert_persisted ? "Yes" : "No"} />
          </Card>
        </div>
      )}

      {editBcs && <BcsForm bcs={editBcs} impacts={meta.impacts} besAssets={besAssets} onClose={() => setEditBcs(null)} onSaved={() => { setEditBcs(null); refresh(); }} />}
      {assetForm && <AssetForm asset={assetForm} types={meta.asset_types} labels={meta.asset_labels} functions={meta.device_functions} onClose={() => setAssetForm(null)}
        onSaved={(bid) => { setAssetForm(null); loadAssets(bid); refresh(); }} />}
      {editBes && <BesAssetForm bes={editBes} types={besTypes} onClose={() => setEditBes(null)} onSaved={() => { setEditBes(null); refresh(); }} />}
    </div>
  );
}

const StatCard = ({ label, value, tone }) => (
  <div className="border border-border p-3" data-testid={`cip002-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="overline text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold ${tone || ""}`}>{value}</div>
  </div>
);
const Card = ({ title, children }) => (
  <div className="border border-border p-4"><div className="overline text-muted-foreground mb-2">{title}</div>{children}</div>
);
const Row = ({ label, value, tone }) => (
  <div className="flex justify-between py-0.5 text-sm"><span className="text-muted-foreground">{label}</span><span className={`font-semibold ${tone || ""}`}>{value}</span></div>
);

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={onClose}>
      <div className={`bg-card border border-border w-full ${wide ? "max-w-2xl" : "max-w-lg"} mx-4`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block text-sm mb-3"><span className="block font-medium mb-1">{label}</span>{children}</label>
);
const inputCls = "w-full border border-border rounded px-2 py-1.5 text-sm";

function BcsForm({ bcs, impacts, besAssets, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", impact_rating: "medium", description: "", categorization_basis: "",
    facility: "", owner: "", bes_asset_id: "", last_reviewed: "", notes: "", ...bcs,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const body = { ...f, bes_asset_id: f.bes_asset_id || null, last_reviewed: f.last_reviewed || null };
    try {
      if (bcs.id) await api.patch(`/cip002/bcs/${bcs.id}`, body);
      else await api.post("/cip002/bcs", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={bcs.id ? "Edit BES Cyber System" : "Add BES Cyber System"} onClose={onClose}>
      <Field label="System name *"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cip002-bcs-name" autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Impact rating *">
          <select className={inputCls} value={f.impact_rating} onChange={(e) => set("impact_rating", e.target.value)} data-testid="cip002-bcs-impact">
            {impacts.map((i) => <option key={i} value={i}>{impactLabel(i)}</option>)}
          </select>
        </Field>
        <Field label="Associated BES Asset">
          <select className={inputCls} value={f.bes_asset_id || ""} onChange={(e) => set("bes_asset_id", e.target.value)} data-testid="cip002-bcs-besasset">
            <option value="">—</option>
            {besAssets.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.asset_type})</option>)}
          </select>
        </Field>
      </div>
      <Field label="Categorization basis (Attachment 1 criterion)"><input className={inputCls} value={f.categorization_basis} onChange={(e) => set("categorization_basis", e.target.value)} data-testid="cip002-bcs-basis" placeholder="e.g. Criterion 2.3 — Control Centers" /></Field>
      <Field label="Last review (date)"><input type="date" className={inputCls} value={(f.last_reviewed || "").slice(0, 10)} onChange={(e) => set("last_reviewed", e.target.value)} data-testid="cip002-bcs-reviewed" /></Field>
      <Field label="Description / notes"><textarea className={inputCls} rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="cip002-bcs-desc" /></Field>
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} data-testid="cip002-bcs-save">Save</Button>
      </div>
    </Modal>
  );
}

function AssetForm({ asset, types, labels, functions, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", asset_type: "BCA", device_type: "", device_function: "", ip_address: "",
    location: "", vendor: "", model: "", os_version: "", notes: "", ...asset,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Asset name is required");
    try {
      if (asset.id) await api.patch(`/cip002/assets/${asset.id}`, f);
      else await api.post("/cip002/assets", { ...f, bcs_id: asset.bcs_id });
      toast.success("Saved"); onSaved(asset.bcs_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={asset.id ? "Edit Cyber Asset" : "Add Cyber Asset"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Asset name *"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cip002-asset-name" autoFocus /></Field>
        <Field label="Classification *">
          <select className={inputCls} value={f.asset_type} onChange={(e) => set("asset_type", e.target.value)} data-testid="cip002-asset-type">
            {types.map((t) => <option key={t} value={t}>{t} — {labels[t] || ""}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Device function">
          <select className={inputCls} value={f.device_function} onChange={(e) => set("device_function", e.target.value)} data-testid="cip002-asset-function">
            <option value="">—</option>
            {(functions || []).map((fn) => <option key={fn} value={fn}>{fn}</option>)}
          </select>
        </Field>
        <Field label="IP address"><input className={inputCls} value={f.ip_address} onChange={(e) => set("ip_address", e.target.value)} data-testid="cip002-asset-ip" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Manufacturer / Vendor"><input className={inputCls} value={f.vendor} onChange={(e) => set("vendor", e.target.value)} data-testid="cip002-asset-vendor" /></Field>
        <Field label="Model"><input className={inputCls} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="cip002-asset-model" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="OS / Firmware version"><input className={inputCls} value={f.os_version} onChange={(e) => set("os_version", e.target.value)} data-testid="cip002-asset-os" /></Field>
        <Field label="Location"><input className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="cip002-asset-location" /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} data-testid="cip002-asset-save">Save</Button>
      </div>
    </Modal>
  );
}

function BesAssetForm({ bes, types, onClose, onSaved }) {
  const [f, setF] = useState({
    bes_asset_id: "", name: "", asset_type: "Substation", description: "", location: "",
    high_impact: false, medium_impact: false, low_impact: false, erc: false, notes: "", ...bes,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    try {
      if (bes.id) await api.patch(`/cip002/bes-assets/${bes.id}`, f);
      else await api.post("/cip002/bes-assets", f);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Stateless render helper; closure captures form state — safe to keep local.
  // eslint-disable-next-line react/no-unstable-nested-components
  const Check = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`cip002-bes-${k}`} />{label}</label>
  );
  return (
    <Modal title={bes.id ? "Edit BES Asset" : "Add BES Asset"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name *"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cip002-bes-name" autoFocus /></Field>
        <Field label="Asset type *">
          <select className={inputCls} value={f.asset_type} onChange={(e) => set("asset_type", e.target.value)} data-testid="cip002-bes-type">
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="BES Asset ID (your label)"><input className={inputCls} value={f.bes_asset_id} onChange={(e) => set("bes_asset_id", e.target.value)} data-testid="cip002-bes-assetid" /></Field>
        <Field label="Location"><input className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="cip002-bes-location" /></Field>
      </div>
      <div className="border border-border p-3 mb-3">
        <div className="overline text-muted-foreground mb-2">Contains impact-rated BCS</div>
        <div className="flex flex-wrap gap-4">
          <Check k="high_impact" label="High" />
          <Check k="medium_impact" label="Medium" />
          <Check k="low_impact" label="Low" />
          <Check k="erc" label="External Routable Connectivity" />
        </div>
      </div>
      <Field label="Description"><textarea className={inputCls} rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="cip002-bes-desc" /></Field>
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} data-testid="cip002-bes-save">Save</Button>
      </div>
    </Modal>
  );
}

function SignOffPanel({ cat, canEdit, onSaved }) {
  const [f, setF] = useState({
    preparer_name: "", preparer_title: "", preparer_is_pe: false, preparer_date: "",
    senior_manager_name: "", senior_manager_date: "", smes_consulted: "", low_method: "named", target_months: 12,
    process_enabled: false, bes_start_month: 10, bcs_target_month: 12, process_assignee: "", pnc_confidential_label: false, notes: "",
    ...cat,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const body = { ...f, target_months: Number(f.target_months) || 12,
        bes_start_month: Number(f.bes_start_month) || 10, bcs_target_month: Number(f.bcs_target_month) || 12,
        preparer_date: f.preparer_date || null };
      delete body.approval; delete body.device_functions; delete body.regulatory_months; delete body.process;
      await api.put("/cip002/categorization", body);
      toast.success("Sign-off saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approve = async () => {
    if (!f.senior_manager_name.trim()) return toast.error("Enter the CIP Senior Manager name first");
    try {
      await api.post("/cip002/categorization/approve", { senior_manager_name: f.senior_manager_name });
      toast.success("CIP Senior Manager approval recorded (R2 clock reset)"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startCycle = async () => {
    if (!window.confirm("Create the ClaroFlow re-identification ticket for the current cycle now?")) return;
    try {
      await api.post("/cip002/process/start");
      toast.success("ClaroFlow ticket created"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approval = cat?.approval;
  const proc = cat?.process;
  const regMonths = cat?.regulatory_months || 15;
  return (
    <div className="border border-border p-4" data-testid="cip002-signoff">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <SealCheck size={18} weight="duotone" />
        <div className="font-semibold text-sm">R1/R2 Sign-off &amp; Approval</div>
        {approval && <span className={`text-xs border px-1.5 py-0.5 rounded ${REVIEW_BADGE[approval.status]}`}>{reviewLabel(approval.status)}{approval.target_due ? ` · target ${approval.target_due}` : ""}</span>}
        {approval?.pnc && <span className="text-xs border border-red-400 bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-semibold" data-testid="cip002-pnc-flag">PNC — past {regMonths} mo</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Prepared by (identifier — often a PE)"><input className={inputCls} value={f.preparer_name} onChange={(e) => set("preparer_name", e.target.value)} data-testid="cip002-preparer-name" disabled={!canEdit} /></Field>
        <Field label="Preparer title"><input className={inputCls} value={f.preparer_title} onChange={(e) => set("preparer_title", e.target.value)} data-testid="cip002-preparer-title" disabled={!canEdit} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!f.preparer_is_pe} onChange={(e) => set("preparer_is_pe", e.target.checked)} data-testid="cip002-preparer-pe" disabled={!canEdit} /> Preparer is a licensed Professional Engineer (PE)</label>
      <Field label="Subject Matter Experts consulted (Tx Planning, P&amp;C / Protection Engineers, Operations…)"><textarea className={inputCls} rows={2} value={f.smes_consulted} onChange={(e) => set("smes_consulted", e.target.value)} data-testid="cip002-smes" disabled={!canEdit} /></Field>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Low BCS identification method">
          <select className={inputCls} value={f.low_method} onChange={(e) => set("low_method", e.target.value)} data-testid="cip002-low-method" disabled={!canEdit}>
            <option value="named">Named systems (like High/Medium)</option>
            <option value="asset_location">By asset / location</option>
          </select>
        </Field>
        <Field label={`Target review cadence (months) — regulatory limit ${regMonths}`}>
          <select className={inputCls} value={f.target_months} onChange={(e) => set("target_months", e.target.value)} data-testid="cip002-target-months" disabled={!canEdit}>
            {[6, 9, 10, 11, 12, 13, 14, 15].map((m) => <option key={m} value={m}>{m} months{m === regMonths ? " (regulatory limit)" : ""}</option>)}
          </select>
        </Field>
        <Field label="CIP Senior Manager (required approver)"><input className={inputCls} value={f.senior_manager_name} onChange={(e) => set("senior_manager_name", e.target.value)} data-testid="cip002-csm-name" disabled={!canEdit} /></Field>
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        The Standard allows up to {regMonths} months (the PNC line). Set an earlier internal target to wrap up ahead of the deadline.
        {approval?.last_reviewed && <> Last approved <b>{approval.last_reviewed}</b> by {cat.senior_manager_name}. Internal target due <b>{approval.target_due}</b>; regulatory (PNC) due <b>{approval.regulatory_due}</b>.</>}
      </div>

      {/* --- Recurring process automation (ClaroFlow) --- */}
      <div className="border-t border-border pt-3 mt-1">
        <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Buildings size={16} /> Recurring Process (ClaroFlow)</div>
        <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!f.process_enabled} onChange={(e) => set("process_enabled", e.target.checked)} data-testid="cip002-process-enabled" disabled={!canEdit} /> Auto-create a ClaroFlow ticket each cycle to re-identify BES Assets &amp; BCS</label>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Start BES Asset evaluation (months after approval)">
            <select className={inputCls} value={f.bes_start_month} onChange={(e) => set("bes_start_month", e.target.value)} data-testid="cip002-bes-start-month" disabled={!canEdit}>
              {[6, 7, 8, 9, 10, 11, 12].map((m) => <option key={m} value={m}>{m} months</option>)}
            </select>
          </Field>
          <Field label="Target to identify BCS (months)">
            <select className={inputCls} value={f.bcs_target_month} onChange={(e) => set("bcs_target_month", e.target.value)} data-testid="cip002-bcs-target-month" disabled={!canEdit}>
              {[9, 10, 11, 12, 13, 14, 15].map((m) => <option key={m} value={m}>{m} months</option>)}
            </select>
          </Field>
          <Field label="Assign ticket to (name/email)"><input className={inputCls} value={f.process_assignee} onChange={(e) => set("process_assignee", e.target.value)} data-testid="cip002-process-assignee" disabled={!canEdit} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={!!f.pnc_confidential_label} onChange={(e) => set("pnc_confidential_label", e.target.checked)} data-testid="cip002-pnc-confidential" disabled={!canEdit} /> Label PNC / compliance-breach alerts "Confidential - Attorney Work Product" (subject line)</label>
        {proc?.anchor ? (
          <div className="text-xs text-muted-foreground mb-2" data-testid="cip002-process-schedule">
            Cycle anchored on {proc.anchor}. <b>Start</b> (preventative) {proc.start_date} · <b>Target / working due</b> {proc.target_date} · <b>Compliance</b> (detective hard line) <b>{proc.compliance_date}</b>.
            {proc.ticket
              ? <> Ticket: <b>{proc.ticket.status}</b> (working due {proc.ticket.due_date}).</>
              : <> No ticket yet — auto-created on {proc.start_date}.</>}
            {proc.compliance_breach && <span className="ml-1 inline-block border border-red-400 bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-semibold" data-testid="cip002-compliance-breach">Compliance date passed — PNC (CSM alerted)</span>}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground mb-2">Record a CIP Senior Manager approval to anchor the recurring cycle.</div>
        )}
      </div>

      {canEdit && (
        <div className="flex justify-end gap-2 flex-wrap">
          {proc?.anchor && !proc?.ticket && <Button variant="outline" size="sm" className="gap-1" onClick={startCycle} data-testid="cip002-start-cycle-btn"><Buildings size={14} /> Start cycle ticket now</Button>}
          <Button variant="outline" size="sm" onClick={save} data-testid="cip002-signoff-save">Save sign-off</Button>
          <Button size="sm" className="gap-1" onClick={approve} data-testid="cip002-approve-btn"><CheckCircle size={14} /> Record CSM approval (today)</Button>
        </div>
      )}
    </div>
  );
}
